import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Button, Tabs, Tab } from "@mui/material";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { offerMappingService, OfferData } from "../../utils/offerMapping";
import { ApiManager } from "../../utils/apiManager";
import { BASE_URL } from "../../config";
import emptyStateGif from "../../assets/Empty_state_ghost_20260105103922.gif";


interface OfferItem {
    url: string;
    name: string;
    isActive: boolean;
    payout?: string;
    status?: string;
    comment?: string;
}

export default function OfferSection({ isPublicPage = false }: { isPublicPage?: boolean }) {
    // const navigate = useNavigate();
    const [tabIndex, setTabIndex] = useState(0); // 0 = Ongoing, 1 = Completed
    const [offers, setOffers] = useState<OfferItem[]>([]);

    const fetchActiveOffers = useCallback(async (): Promise<void> => {
        console.log("Fetching active offers...");
        try {
            // First, fetch completed offers from local storage to filter them out
            const localHistoryStr = localStorage.getItem('local_offer_history');
            const localHistory = localHistoryStr ? JSON.parse(localHistoryStr) : [];
            const userIdentifier = localStorage.getItem('phone') || localStorage.getItem('email');
            const completedItems = localHistory.filter((item: any) => item.phone === userIdentifier);

            const token = localStorage.getItem('auth_token');
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await ApiManager.fetchWithAbort(
                `${BASE_URL}/api/form/v2/check-form/?url=0`,
                { headers }
            );

            if (response.status === 200) {
                const data = await response.json();
                console.log("Active Offers API Data:", data);

                let globalAmount: string | null = null;
                if (data.amount !== undefined && data.amount !== null) {
                    globalAmount = data.amount.toString();
                    localStorage.setItem('current_offer_amount', globalAmount || ""); // Store global for fallback
                }

                let activeForms: OfferData[] = [];
                // Map to store temporary amounts per URL if provided in active_forms
                const specificAmounts: Record<string, string> = {};

                if (data.active_forms && Array.isArray(data.active_forms)) {
                    activeForms = data.active_forms.map((item: any) => {
                        if (item.amount) {
                            specificAmounts[item.url] = item.amount.toString();
                        }
                        return {
                            url: item.url,
                            name: item.name,
                            is_public: item.is_public
                        };
                    });
                }

                offerMappingService.updateOffers(activeForms);

                const allOffers: OfferItem[] = [];
                if (activeForms.length > 0) {
                    activeForms.forEach((form) => {
                        const urlStr = form.url;
                        // Use the real name from the form, fallback to "Special Offer" only if completely missing
                        const rawName = form.name || offerMappingService.getAppName(urlStr) || "Special Offer";

                        // Check completion
                        const matchingCompletion = completedItems.find((item: any) => (item.name?.toLowerCase() || '') === rawName.toLowerCase());
                        let isCompleted = !!matchingCompletion;

                        // Re-show rejected public offers
                        if (isCompleted && matchingCompletion.status === 'rejected') {
                            const isOfferPublic = offerMappingService.isOfferPublic(urlStr);
                            if (!isOfferPublic) {
                                isCompleted = false;
                            }
                        }

                        // If completed, we can change name or keep logic. 
                        const displayName = isCompleted ? "Special Offer" : (rawName === "Unknown App" ? "Special Offer" : rawName);

                        // Determine payout
                        let payoutAmount = globalAmount;
                        if (specificAmounts[urlStr]) {
                            payoutAmount = specificAmounts[urlStr];
                        }

                        if (!isCompleted) {
                            allOffers.push({
                                url: urlStr,
                                name: displayName,
                                isActive: true, 
                                payout: payoutAmount ? `$${payoutAmount}` : "..."
                            });
                        }
                    });
                }

                // Sort: Active offers first
                allOffers.sort((a, b) => {
                    if (a.isActive === b.isActive) return 0;
                    return a.isActive ? -1 : 1;
                });

                setOffers(allOffers);
            } else {
                console.warn("Active Offers API returned status:", response.status);
            }
        } catch (err) {
            console.warn("Error fetching offers:", err);
        }
    }, []);

    useEffect(() => {
        fetchActiveOffers();

        // Check if we should auto-switch to completed tab (after form submission)
        const shouldRefresh = localStorage.getItem('refresh_completed_offers');
        if (shouldRefresh === 'true') {
            // Clear the flag
            localStorage.removeItem('refresh_completed_offers');
            // Switch to completed tab
            setTabIndex(1);
        }
    }, [fetchActiveOffers]);


    const [completedOffers, setCompletedOffers] = useState<OfferItem[]>([]);

    const fetchCompletedOffers = async () => {
        // First check for auth token
        const token = localStorage.getItem('auth_token');
        // Get user identifier from localStorage
        const userIdentifier = localStorage.getItem('phone') || localStorage.getItem('email');

        console.log("Attempting to fetch completed offers...", { hasToken: !!token, userIdentifier });

        // Get local history for fallback
        const localHistoryStr = localStorage.getItem('local_offer_history');
        const localHistory = localHistoryStr ? JSON.parse(localHistoryStr) : [];
        const localUserHistory = localHistory.filter((item: any) => item.phone === userIdentifier);
        const mappedLocal = localUserHistory.map((item: any) => ({
            url: '',
            name: item.name || 'Offer',
            isActive: false,
            payout: item.amount ? `$${item.amount}` : 'Paid'
        }));

        // DISABLED: API call to completed-offers
        // Just use local storage data
        if (mappedLocal.length > 0) {
            setCompletedOffers(mappedLocal);
        } else {
            console.log("No completed offers found in local storage.");
            setCompletedOffers([]);
        }
    };

    useEffect(() => {
        if (tabIndex === 1) {
            fetchCompletedOffers();
        }
    }, [tabIndex]);


    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    return (
        <Box sx={{ width: '100%', px: 2, pb: 4, backgroundColor: '#fff' }} id="offers-section">

            {/* Title */}
            <Typography
                variant="h5"
                sx={{
                    textAlign: 'center',
                    fontWeight: 700,
                    color: '#1A237E',
                    mb: 3,
                    fontFamily: "General-Sans-bold, sans-serif"
                }}
            >
                Today's Offers
            </Typography>

            {/* Tabs */}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Tabs
                    value={tabIndex}
                    onChange={handleTabChange}
                    aria-label="offer tabs"
                    TabIndicatorProps={{ style: { display: 'none' } }}
                    sx={{
                        '& .MuiTabs-flexContainer': { gap: { xs: '8px', sm: '16px' } }
                    }}
                >
                    <Tab
                        label="Ongoing Offers"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '8px',
                            background: tabIndex === 0 ? 'linear-gradient(90deg, #FFC107 0%, #FB8C00 100%)' : '#fff',
                            color: tabIndex === 0 ? '#fff !important' : '#FB8C00',
                            boxShadow: tabIndex === 0 ? '0 4px 12px rgba(251, 140, 0, 0.3)' : 'none',
                            px: { xs: 2, sm: 3 },
                            py: 1,
                            minHeight: 'auto',
                            fontSize: { xs: '14px', sm: '16px' },
                            fontFamily: "General-Sans-bold, sans-serif"
                        }}
                    />
                    <Tab
                        label="Completed Offers"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            borderRadius: '8px',
                            background: tabIndex === 1 ? 'linear-gradient(90deg, #FFC107 0%, #FB8C00 100%)' : '#fff',
                            color: tabIndex === 1 ? '#fff !important' : '#FB8C00',
                            boxShadow: tabIndex === 1 ? '0 4px 12px rgba(251, 140, 0, 0.3)' : 'none',
                            px: { xs: 2, sm: 3 },
                            py: 1,
                            minHeight: 'auto',
                            fontSize: { xs: '14px', sm: '16px' },
                            fontFamily: "General-Sans-bold, sans-serif"
                        }}
                    />
                </Tabs>
            </Box>

            {/* Offers List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {tabIndex === 0 ? (
                    // ONGOING TAB - Show all offers (active ones unlocked, completed/unavailable ones locked)
                    offers.map((offer, index) => (
                        <OfferCard key={index} offer={offer} />
                    ))
                ) : (
                    // COMPLETED TAB
                    completedOffers.length > 0 ? (
                        completedOffers.map((offer, index) => (
                            <OfferCard key={index} offer={offer} />
                        ))
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4, mb: 4 }}>
                            <Box sx={{ height: 200, width: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img
                                    src={emptyStateGif}
                                    alt="No completed offers"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                />
                            </Box>
                            <Typography variant="h6" sx={{ color: '#9E9E9E', mt: 2, fontWeight: 500 }}>
                                No Completed Offer
                            </Typography>
                        </Box>
                    )
                )}
            </Box>

            {tabIndex === 0 && (
                <Typography
                    onClick={() => window.location.href = "https://offer.app20.co/apk"}
                    sx={{
                        textAlign: 'center',
                        fontWeight: 600,
                        color: '#FB8C00',
                        mt: 2,
                        cursor: 'pointer',
                        fontSize: '14px',
                        textDecoration: 'underline'
                    }}
                >
                    And Explore more such offers...
                </Typography>
            )}

        </Box>
    );
}

function OfferCard({ offer }: { offer: OfferItem }) {
    const isLocked = !offer.isActive;

    return (
        <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Lock Sidebar */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Box sx={{
                    color: isLocked ? '#1A237E' : '#4CAF50',
                    mb: 0.5
                }}>
                    {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                </Box>
                <Box sx={{
                    width: '4px',
                    flexGrow: 1,
                    backgroundColor: '#E0E0E0',
                    borderRadius: '2px'
                }} />
            </Box>

            {/* Card Content */}
            <Box
                sx={{
                    flex: 1,
                    backgroundColor: '#fff',
                    border: '2px solid',
                    borderColor: offer.isActive ? '#FFD54F' : '#9E9E9E', // Yellow border for active, Grey for inactive
                    borderRadius: '12px',
                    p: 2,
                    position: 'relative',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    opacity: isLocked ? 0.8 : 1
                }}
            >
                {/* Header Row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 700, color: '#333' }}>
                            {offer.name}
                        </Typography>
                        {!offer.isActive && offer.status && (
                            <Box sx={{ mt: 0.5 }}>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: offer.status === 'approved' ? '#4CAF50' :
                                            offer.status === 'pending' ? '#FF9800' : '#F44336',
                                        backgroundColor: offer.status === 'approved' ? '#E8F5E9' :
                                            offer.status === 'pending' ? '#FFF3E0' : '#FFEBEE',
                                        px: 1,
                                        py: 0.3,
                                        borderRadius: '4px',
                                        textTransform: 'capitalize'
                                    }}
                                >
                                    {offer.status}
                                </Typography>
                            </Box>
                        )}
                        {!offer.isActive && offer.comment && offer.status !== 'approved' && (
                            <Typography variant="body2" sx={{ fontSize: '11px', color: '#666', mt: 0.5, lineHeight: 1.3, fontStyle: 'italic' }}>
                                Comment: {offer.comment}
                            </Typography>
                        )}
                        {offer.isActive && (
                            <Typography variant="body2" sx={{ fontSize: '11px', color: '#666', mt: 0.5, lineHeight: 1.3 }}>
                                Complete this offer, install, review and post to unlock more offers.
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 800, color: '#2E7D32', ml: 1 }}>
                        {offer.payout}
                    </Typography>
                </Box>

                {/* Button */}
                <Button
                    fullWidth
                    disabled={isLocked}
                    onClick={() => {
                        window.location.href = `/offer-install/${offer.url}`;
                    }}
                    sx={{
                        backgroundColor: isLocked ? '#fff' : '#FFD54F', // Yellow background if active
                        color: isLocked ? '#000' : '#000',
                        border: isLocked ? '1px solid #000' : 'none',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: '20px',
                        py: 0.5,
                        mt: 1,
                        fontSize: '14px',
                        boxShadow: isLocked ? 'none' : '0 2px 5px rgba(0,0,0,0.1)',
                        "&:hover": {
                            backgroundColor: isLocked ? '#fff' : '#FFC107'
                        }
                    }}
                >
                    Start Now
                </Button>
            </Box>
        </Box>
    );
}